import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  Wand2,
  ImageIcon,
  Save,
  Lightbulb,
  X,
  Library,
} from "lucide-react";
import ImagePickerDialog from "@/components/ImagePickerDialog";

const CATEGORIES = ["Computer Vision", "LLMs", "VoiceBots", "General AI"];
const TONES = ["Professional", "Conversational", "Thought-Leadership", "Educational"];
const LENGTHS = ["Short", "Medium", "Long"];

export default function ComposerPanel({ open, onClose, onSaved, article }) {
  const [stage, setStage] = useState("input"); // input | preview
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("Computer Vision");
  const [tone, setTone] = useState("Thought-Leadership");
  const [length, setLength] = useState("Medium");
  const [personaId, setPersonaId] = useState("");
  const [personas, setPersonas] = useState([]);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [cover, setCover] = useState(null);
  const [articleId, setArticleId] = useState(null);
  const [articleStatus, setArticleStatus] = useState("draft");

  const [genLoading, setGenLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [topicLoading, setTopicLoading] = useState(false);
  const [topicSuggestions, setTopicSuggestions] = useState([]);

  // Reset / hydrate when opening
  useEffect(() => {
    if (!open) return;
    // Load personas (active only)
    api.get("/personas").then(({ data }) => {
      const active = data.filter((p) => p.is_active);
      setPersonas(active);
      // Default to Marine if available
      const marine = active.find((p) => p.name === "Marine");
      if (marine && !personaId) setPersonaId(marine.id);
      else if (active[0] && !personaId) setPersonaId(active[0].id);
    }).catch(() => {});
    if (article) {
      setStage("preview");
      setTopic(article.topic || "");
      setNotes(article.notes || "");
      setCategory(article.category || "General AI");
      setTitle(article.title || "");
      setBody(article.body || "");
      setHashtags(article.hashtags || []);
      setCover(article.cover_image || null);
      setArticleId(article.id);
      setArticleStatus(article.status || "draft");
    } else {
      setStage("input");
      setTopic("");
      setNotes("");
      setCategory("Computer Vision");
      setTone("Thought-Leadership");
      setLength("Medium");
      setTitle("");
      setBody("");
      setHashtags([]);
      setCover(null);
      setArticleId(null);
      setArticleStatus("draft");
      setTopicSuggestions([]);
    }
  }, [open, article]);

  const suggestTopics = async () => {
    setTopicLoading(true);
    try {
      const [s, r] = await Promise.allSettled([
        api.post("/topics/suggest", { category }),
        api.get("/recommendations/topics?limit=4"),
      ]);
      const base = s.status === "fulfilled" ? (s.value.data.topics || []) : [];
      const recos = r.status === "fulfilled" && r.value.data.ready ? (r.value.data.topics || []) : [];
      // Personalised first (with a sparkle marker), then generic suggestions, dedup
      const merged = [];
      const seen = new Set();
      for (const x of recos) {
        const t = (x.topic || "").trim();
        if (!t || seen.has(t.toLowerCase())) continue;
        seen.add(t.toLowerCase());
        merged.push(`✨ ${t}`);
      }
      for (const t of base) {
        if (!t || seen.has(String(t).toLowerCase())) continue;
        seen.add(String(t).toLowerCase());
        merged.push(t);
      }
      setTopicSuggestions(merged.slice(0, 8));
    } catch (e) {
      toast.error("Topic suggestion failed");
    } finally {
      setTopicLoading(false);
    }
  };

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Topic is required");
      return;
    }
    setGenLoading(true);
    try {
      const { data } = await api.post("/articles/generate", {
        topic, notes, category, tone, length, persona_id: personaId || undefined,
      });
      setTitle(data.title);
      setBody(data.body);
      setHashtags(data.hashtags || []);
      setStage("preview");
      toast.success("Article drafted by Lingent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenLoading(false);
    }
  };

  const generateImage = async () => {
    setImgLoading(true);
    try {
      const { data } = await api.post("/articles/generate-image", {
        prompt: `${title || topic} (${category})`,
      });
      setCover(data.image);
      toast.success("Cover image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally {
      setImgLoading(false);
    }
  };

  const save = async (newStatus) => {
    setSaveLoading(true);
    try {
      let saved;
      if (articleId) {
        const payload = { title, body, hashtags, cover_image: cover };
        if (newStatus) payload.status = newStatus;
        const { data } = await api.patch(`/articles/${articleId}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/articles", {
          topic, notes, category, title, body, hashtags, cover_image: cover,
        });
        saved = data;
        setArticleId(data.id);
        if (newStatus) {
          const { data: updated } = await api.patch(`/articles/${data.id}`, { status: newStatus });
          saved = updated;
        }
      }
      setArticleStatus(saved.status);
      toast.success(newStatus ? `Marked as ${newStatus}` : "Saved as draft");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const copyAndOpenLinkedIn = async () => {
    const text = `${title}\n\n${body}\n\n${hashtags.join(" ")}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied. Opening LinkedIn…");
    } catch {
      toast.message("Copy manually — clipboard blocked");
    }
    await save("published");
    window.open("https://www.linkedin.com/company/wisewit-solutions/", "_blank");
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        data-testid="composer-panel"
        className="w-full sm:max-w-2xl bg-ink border-l border-hairline text-bone p-0 rounded-none overflow-y-auto scrollbar-thin"
      >
        <SheetHeader className="px-6 py-5 border-b border-hairline sticky top-0 bg-ink z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-safety flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <SheetTitle className="font-display tracking-tightest text-bone text-xl">
                  {stage === "input" ? "Brief Lingent" : title || "Article Preview"}
                </SheetTitle>
                <SheetDescription className="label-mono text-[10px] text-bone/50 mt-1">
                  {stage === "input" ? "Step 1 — Topic & Notes" : `Step 2 — Preview · ${articleStatus.toUpperCase()}`}
                </SheetDescription>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="composer-close-btn"
              className="text-bone/60 hover:text-bone"
              aria-label="close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </SheetHeader>

        <div className="px-6 py-6 space-y-6">
          {stage === "input" && (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="label-mono text-bone/70">Topic</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={suggestTopics}
                    disabled={topicLoading}
                    data-testid="suggest-topics-btn"
                    className="label-mono text-safety hover:text-safety hover:bg-surface rounded-none"
                  >
                    {topicLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lightbulb className="w-3 h-3 mr-1" />}
                    Suggest
                  </Button>
                </div>
                <Input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. Vision Transformers for defect detection on bottling lines"
                  className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
                  data-testid="composer-topic-input"
                />
                {topicSuggestions.length > 0 && (
                  <div className="mt-3 space-y-2 border border-hairline bg-surface p-3" data-testid="topic-suggestions">
                    <div className="label-mono text-bone/50 mb-1">Pick one:</div>
                    {topicSuggestions.map((t, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setTopic(t.replace(/^✨\s*/, "")); setTopicSuggestions([]); }}
                        className="block w-full text-left text-sm text-bone/80 hover:text-bone hover:bg-surface-2 p-2 transition"
                        data-testid={`topic-suggestion-${i}`}
                      >
                        → {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="label-mono text-bone/70">Key Points / Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  placeholder="Add stats, customer anecdotes, links, or specific angles you want Lingent to weave in."
                  className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein"
                  data-testid="composer-notes-input"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="label-mono text-bone/70">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-2 rounded-none bg-surface border-hairline" data-testid="composer-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-surface border-hairline">
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono text-bone/70">Tone</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="mt-2 rounded-none bg-surface border-hairline" data-testid="composer-tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-surface border-hairline">
                      {TONES.map((t) => (
                        <SelectItem key={t} value={t} className="rounded-none">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono text-bone/70">Length</Label>
                  <Select value={length} onValueChange={setLength}>
                    <SelectTrigger className="mt-2 rounded-none bg-surface border-hairline" data-testid="composer-length-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-surface border-hairline">
                      {LENGTHS.map((l) => (
                        <SelectItem key={l} value={l} className="rounded-none">{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {personas.length > 0 && (
                <div>
                  <Label className="label-mono text-bone/70">Industry persona</Label>
                  <Select value={personaId || "none"} onValueChange={(v) => setPersonaId(v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-2 rounded-none bg-surface border-hairline" data-testid="composer-persona-select">
                      <SelectValue placeholder="Choose persona…" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-surface border-hairline">
                      <SelectItem value="none" className="rounded-none">(no persona — generic)</SelectItem>
                      {personas.map((p) => (
                        <SelectItem key={p.id} value={p.id} className="rounded-none">
                          {p.name} {p.industry_label ? `· ${p.industry_label}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="label-mono text-[10px] text-bone/40 mt-1">
                    Injects industry jargon, regulatory anchors and CTA into the draft.
                  </div>
                </div>
              )}

              <Button
                onClick={generate}
                disabled={genLoading || !topic.trim()}
                data-testid="composer-generate-btn"
                className="w-full rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-12"
              >
                {genLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                {genLoading ? "Drafting…" : "Draft with Lingent"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => setPickerOpen(true)}
                data-testid="composer-pick-image-step1-btn"
                className="w-full rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-10"
              >
                <Library className="w-4 h-4 mr-2" />
                {cover ? "Change cover from library" : "Or pick a cover from library first"}
              </Button>
              {cover && (
                <div className="border border-hairline">
                  <img src={cover} alt="cover preview" className="w-full max-h-40 object-cover" />
                </div>
              )}
            </div>
          )}

          {stage === "preview" && (
            <div className="space-y-5">
              {cover && (
                <div className="border border-hairline">
                  <img src={cover} alt="cover" className="w-full max-h-72 object-cover" data-testid="composer-cover-image" />
                </div>
              )}

              <div>
                <Label className="label-mono text-bone/70">Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
                  data-testid="composer-title-input"
                />
              </div>

              <div>
                <Label className="label-mono text-bone/70">Body</Label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={16}
                  className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein font-sans leading-relaxed"
                  data-testid="composer-body-input"
                />
              </div>

              <div>
                <Label className="label-mono text-bone/70">Hashtags</Label>
                <div className="mt-2 flex flex-wrap gap-2 min-h-[40px] p-2 border border-hairline bg-surface" data-testid="composer-hashtags">
                  {hashtags.map((h, i) => (
                    <Badge key={i} className="rounded-none bg-klein text-white border-none label-mono">{h}</Badge>
                  ))}
                  {hashtags.length === 0 && (
                    <span className="text-bone/40 text-xs label-mono">no hashtags</span>
                  )}
                </div>
                <Input
                  placeholder="Add hashtag and press Enter (e.g. #AIinManufacturing)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      e.preventDefault();
                      const v = e.target.value.trim();
                      const tag = v.startsWith("#") ? v : `#${v}`;
                      setHashtags([...hashtags, tag]);
                      e.target.value = "";
                    }
                  }}
                  className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10"
                  data-testid="composer-add-hashtag"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2 border-t border-hairline">
                <Button
                  variant="outline"
                  onClick={() => setStage("input")}
                  data-testid="composer-back-btn"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  onClick={generate}
                  disabled={genLoading}
                  data-testid="composer-regenerate-btn"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
                >
                  {genLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Regenerate
                </Button>
                <Button
                  variant="outline"
                  onClick={generateImage}
                  disabled={imgLoading}
                  data-testid="composer-gen-image-btn"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
                >
                  {imgLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                  {cover ? "Re-gen image" : "Generate cover"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPickerOpen(true)}
                  data-testid="composer-pick-image-btn"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
                >
                  <Library className="w-4 h-4 mr-2" />
                  Pick from library
                </Button>
                <Button
                  variant="outline"
                  onClick={() => save("draft")}
                  disabled={saveLoading}
                  data-testid="composer-save-draft-btn"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
                >
                  {saveLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Draft
                </Button>
                <Button
                  onClick={() => save("approved")}
                  disabled={saveLoading}
                  data-testid="composer-approve-btn"
                  className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono"
                >
                  Approve
                </Button>
                <Button
                  onClick={copyAndOpenLinkedIn}
                  disabled={saveLoading}
                  data-testid="composer-publish-btn"
                  className="rounded-none bg-safety hover:bg-safety/90 text-white label-mono"
                >
                  Copy + Open LinkedIn
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
      <ImagePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => { setCover(url); toast.success("Cover updated"); }}
        defaultQuery={`${category} ${topic}`.slice(0, 60)}
      />
    </Sheet>
  );
}
